import React from 'react';
import { View, StyleSheet } from 'react-native';
import {
  SkeletonGroup,
  SkeletonBone,
  SkeletonCard,
  SkeletonList,
  SkeletonListItem,
} from '@/components/common/skeleton';

interface Props {
  isTablet?: boolean;
}

const INNER_GAP = 16;
const GRID_GAP = 12;

/** Placeholder shapes for one compact icon-stat card (icon tile + 2 lines). */
const CompactCardBone: React.FC = () => (
  <SkeletonCard padding={12} borderRadius={16} style={styles.compactCard}>
    <SkeletonBone width={32} height={32} borderRadius={10} />
    <View style={styles.compactCardText}>
      <SkeletonBone width="75%" height={14} />
      <SkeletonBone width="55%" height={12} />
    </View>
  </SkeletonCard>
);

/** Placeholder for one full-width "Coming Up" row. */
const ComingUpRowBone: React.FC = () => (
  <SkeletonCard padding={12} borderRadius={16} style={styles.comingUpRow}>
    <SkeletonBone width={32} height={32} borderRadius={12} />
    <SkeletonBone width="45%" height={14} />
    <View style={styles.spacer} />
    <SkeletonBone width={56} height={14} />
  </SkeletonCard>
);

/**
 * Loading state for `DesktopHomeView`. Mirrors the no-scroll layout it stands
 * in for: a greeting, then a flexed row of a full-height planner card and a
 * narrow column of compact cards and rows.
 */
export const DesktopHomeViewSkeleton: React.FC<Props> = ({ isTablet }) => {
  const useStacked = isTablet;

  return (
    <SkeletonGroup style={styles.container}>
      {/* Greeting */}
      <View style={styles.greetingSection}>
        <SkeletonBone width={200} height={24} />
        <SkeletonBone width={140} height={14} />
      </View>

      <View
        style={[
          styles.columns,
          useStacked ? styles.stackedColumns : styles.rowColumns,
        ]}
      >
        {/* Left: the planner, which fills the window height */}
        <View style={styles.columnPrimary}>
          <SkeletonBone width={100} height={18} />
          <SkeletonCard style={styles.plannerCard}>
            <SkeletonBone width={180} height={32} borderRadius={10} />
            <SkeletonBone width={140} height={18} />
            <SkeletonList count={4} gap={GRID_GAP}>
              <SkeletonListItem
                avatar
                avatarSize={22}
                lines={1}
                lineWidths={['60%']}
              />
            </SkeletonList>
          </SkeletonCard>
        </View>

        {/* Right: Quick Actions grid, Coming Up rows, My Community grid */}
        <View style={styles.columnSecondary}>
          <View style={styles.sectionBlock}>
            <SkeletonBone width={110} height={18} />
            <View style={styles.cardGrid}>
              {Array.from({ length: 4 }).map((_, i) => (
                <View key={i} style={styles.gridItem}>
                  <CompactCardBone />
                </View>
              ))}
            </View>
          </View>

          <View style={styles.sectionBlock}>
            <SkeletonBone width={90} height={18} />
            <View style={styles.rowStack}>
              <ComingUpRowBone />
              <ComingUpRowBone />
            </View>
          </View>

          <View style={styles.sectionBlock}>
            <SkeletonBone width={120} height={18} />
            <View style={styles.cardGrid}>
              {Array.from({ length: 4 }).map((_, i) => (
                <View key={i} style={styles.gridItem}>
                  <CompactCardBone />
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
    </SkeletonGroup>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: INNER_GAP,
  },
  greetingSection: {
    gap: 6,
  },
  columns: {
    flex: 1,
    minHeight: 0,
    gap: INNER_GAP,
  },
  rowColumns: {
    flexDirection: 'row',
  },
  stackedColumns: {
    flexDirection: 'column',
  },
  columnPrimary: {
    flex: 3,
    minHeight: 0,
    gap: GRID_GAP,
  },
  columnSecondary: {
    flex: 2,
    minHeight: 0,
    gap: INNER_GAP,
  },
  plannerCard: {
    flex: 1,
    minHeight: 0,
  },
  sectionBlock: {
    gap: GRID_GAP,
  },
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: GRID_GAP,
  },
  gridItem: {
    flexBasis: '48%',
    flexGrow: 1,
  },
  compactCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: GRID_GAP,
  },
  compactCardText: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  comingUpRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: GRID_GAP,
  },
  rowStack: {
    gap: GRID_GAP,
  },
  spacer: {
    flex: 1,
  },
});
